import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import { registerVitePreloadRecovery } from './lib/vitePreloadRecovery.js';
import './styles.css';
import './booking-refresh.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const stripSensitiveUrlParts = (value) => {
  const input = String(value || '').trim();
  if (!input) return input;

  try {
    const url = new URL(input, window.location.origin);
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch (error) {
    return input.replace(/[?#].*$/, '');
  }
};

const scrubEventUrls = (event) => {
  if (event?.request?.url) {
    event.request.url = stripSensitiveUrlParts(event.request.url);
  }

  if (event?.contexts?.trace?.data?.url) {
    event.contexts.trace.data.url = stripSensitiveUrlParts(event.contexts.trace.data.url);
  }

  if (Array.isArray(event?.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (breadcrumb?.data?.url) {
        breadcrumb.data.url = stripSensitiveUrlParts(breadcrumb.data.url);
      }
      if (breadcrumb?.data?.from) {
        breadcrumb.data.from = stripSensitiveUrlParts(breadcrumb.data.from);
      }
      if (breadcrumb?.data?.to) {
        breadcrumb.data.to = stripSensitiveUrlParts(breadcrumb.data.to);
      }
      return breadcrumb;
    });
  }

  return event;
};

const scrubBreadcrumb = (breadcrumb) => {
  if (breadcrumb?.data?.url) {
    breadcrumb.data.url = stripSensitiveUrlParts(breadcrumb.data.url);
  }
  if (breadcrumb?.data?.from) {
    breadcrumb.data.from = stripSensitiveUrlParts(breadcrumb.data.from);
  }
  if (breadcrumb?.data?.to) {
    breadcrumb.data.to = stripSensitiveUrlParts(breadcrumb.data.to);
  }
  return breadcrumb;
};

const initSentry = () => {
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.05,
      tracePropagationTargets: ['localhost'],
      enableLogs: false,
      beforeSend: scrubEventUrls,
      beforeSendTransaction: scrubEventUrls,
      beforeBreadcrumb: scrubBreadcrumb,
    });
  });
};

const root = createRoot(document.getElementById('root'));

registerVitePreloadRecovery();

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

if (typeof window !== 'undefined') {
  const scheduleInit =
    window.requestIdleCallback ||
    ((callback) => {
      window.setTimeout(callback, 1);
    });
  scheduleInit(() => {
    initSentry();
  });
}
